/**
 * NumberPropertyEditor
 *
 * Reusable number input component for property editors
 * SRP: Responsible ONLY for rendering a number input and handling changes
 * Used by: step, min, max, padding, margin, width, height, etc.
 */

import React, { useCallback, useMemo } from 'react';

export interface NumberPropertyEditorProps {
  /** Display label for the input */
  label: string;

  /** Current value */
  value: number;

  /** Called when value changes */
  onChange: (value: number) => void;

  /** Minimum allowed value */
  min?: number;

  /** Maximum allowed value */
  max?: number;

  /** Step between values */
  step?: number;

  /** Field is required */
  required?: boolean;

  /** Field is disabled */
  disabled?: boolean;

  /** Help text shown below input */
  helpText?: string;

  /** Suffix to display (e.g., 'px', '%', 'ms') */
  suffix?: string;

  /** Custom CSS class */
  className?: string;
}

/**
 * NumberPropertyEditor component
 * Renders a number input with optional min/max/step constraints
 */
export const NumberPropertyEditor: React.FC<NumberPropertyEditorProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  required = false,
  disabled = false,
  helpText,
  suffix,
  className = '',
}) => {
  // Memoize handler to prevent unnecessary re-renders
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = parseFloat(event.currentTarget.value);
      if (!isNaN(newValue)) {
        onChange(newValue);
      }
    },
    [onChange],
  );

  const inputId = useMemo(() => `number-editor-${label.replace(/\s+/g, '-').toLowerCase()}`, [label]);

  // Validate constraints for display purposes
  const isOutOfRange = useMemo(() => {
    if (min !== undefined && value < min) return true;
    if (max !== undefined && value > max) return true;
    return false;
  }, [value, min, max]);

  return (
    <div className={`af-property-editor__group ${className}`}>
      <label htmlFor={inputId} className="af-property-editor__label">
        {label}
        {required && <span className="af-property-editor__required">*</span>}
      </label>

      <div className="af-property-editor__number-wrapper">
        <input
          id={inputId}
          type="number"
          value={value}
          onChange={handleChange}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          required={required}
          className={`af-property-editor__input af-property-editor__input--number ${
            isOutOfRange ? 'af-property-editor__input--error' : ''
          }`}
          aria-label={label}
          aria-required={required}
          aria-invalid={isOutOfRange}
          aria-describedby={helpText ? `${inputId}-help` : undefined}
        />
        {suffix && <span className="af-property-editor__suffix">{suffix}</span>}
      </div>

      {isOutOfRange && (
        <div className="af-property-editor__error">
          Value must be between {min} and {max}
        </div>
      )}

      {helpText && (
        <div id={`${inputId}-help`} className="af-property-editor__help">
          {helpText}
        </div>
      )}
    </div>
  );
};

NumberPropertyEditor.displayName = 'NumberPropertyEditor';
